// ===========================================
// HOOK: Permisos del Usuario (Multi-Tenant)
// ===========================================
//
// Este hook lee permisos desde usuario_organizaciones (sistema multi-tenant)
// en lugar de roles_usuario (legacy).
//
// Mantiene la misma API para compatibilidad con componentes existentes.
//

import { useMemo, type ReactNode } from 'react'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useOverridesRol } from '@/hooks/useUsuarios'
import {
  type RolMultiTenant,
  type Rol,
  type Permisos,
  calcularPermisosEfectivos,
  getPermisosVacios,
  esRolAdmin,
  ROL_MULTITENANT_TO_LEGACY,
} from '@/types/roles'

// ===========================================
// Hook Principal
// ===========================================

/**
 * Hook para verificar permisos del usuario actual
 *
 * Lee los permisos desde usuario_organizaciones (sistema multi-tenant)
 * y calcula permisos efectivos considerando permisos_extra y permisos_denegados.
 *
 * @example
 * const { puede, rol, isLoading } = usePermissions()
 *
 * if (puede.crearCliente) {
 *   // Mostrar boton de crear
 * }
 *
 * if (esAdmin) {
 *   // Mostrar opciones de admin
 * }
 */
export function usePermissions() {
  const { membresia, usuario, isLoading, error } = useOrganizacion()
  // Capa org-rol (política del rol para la organización). Cacheada por org y
  // compartida con el editor. Mientras carga se aplican solo base + extra +
  // denegados: una query cuyo select depende de un permiso espera a permisosListos.
  const overrides = useOverridesRol()
  const overridesRol = overrides.data
  // Membresía y overrides del rol resueltos. Si los overrides fallan, quedan los permisos sin ellos.
  const permisosListos = !isLoading && (!membresia?.activo || overrides.isSuccess || overrides.isError)

  // Calcular permisos basados en la membresía
  const permisos = useMemo(() => {
    if (!membresia || !membresia.activo) {
      return getPermisosVacios()
    }

    // Mapa de overrides org-rol para el rol legacy del usuario actual
    const rolLegacy = ROL_MULTITENANT_TO_LEGACY[membresia.rol as RolMultiTenant]
    const overridesDelRol = (overridesRol ?? [])
      .filter((o) => o.rol === rolLegacy)
      .reduce<Partial<Record<keyof Permisos, boolean>>>((acc, o) => {
        acc[o.flag as keyof Permisos] = o.permitido
        return acc
      }, {})

    return calcularPermisosEfectivos(
      membresia.rol as RolMultiTenant,
      membresia.permisos_extra as Partial<Permisos> | null,
      membresia.permisos_denegados as (keyof Permisos)[] | null,
      overridesDelRol
    )
  }, [membresia, overridesRol])

  // Rol actual (multi-tenant)
  const rolMultiTenant = membresia?.rol as RolMultiTenant | null

  // Para compatibilidad: mapear a rol legacy
  const rolLegacy = rolMultiTenant ? ROL_MULTITENANT_TO_LEGACY[rolMultiTenant] : null

  return {
    // Permisos individuales
    puede: permisos,

    // Rol actual (retorna el rol legacy para compatibilidad)
    // Componentes existentes usan: rol === 'contador_principal'
    rol: rolLegacy,

    // Rol multi-tenant (owner, admin, profesional, asistente, solo_lectura)
    rolMultiTenant,

    // Nombre del usuario
    nombreUsuario: usuario?.nombre || null,

    // Email del usuario
    emailUsuario: usuario?.email || null,

    // Estado del usuario
    usuarioActivo: membresia?.activo ?? false,

    // Estados de carga. isLoading no espera a los overrides del rol; permisosListos sí.
    isLoading,
    permisosListos,
    isAuthenticated: !!usuario,
    error,

    // Helpers de verificacion rapida
    esAdmin: rolMultiTenant ? esRolAdmin(rolMultiTenant) : false,
    esOwner: rolMultiTenant === 'owner',
    esContador:
      rolMultiTenant === 'admin' ||
      rolMultiTenant === 'owner' ||
      rolMultiTenant === 'profesional',
    esSoloLectura: rolMultiTenant === 'solo_lectura',
    esAsistente: rolMultiTenant === 'asistente',

    // Verificar un permiso especifico
    tienePermiso: (permiso: keyof Permisos) => permisos[permiso] === true,

    // Verificar multiples permisos (AND)
    tienePermisos: (permisosList: (keyof Permisos)[]) =>
      permisosList.every((p) => permisos[p] === true),

    // Verificar al menos un permiso (OR)
    tieneAlgunPermiso: (permisosList: (keyof Permisos)[]) =>
      permisosList.some((p) => permisos[p] === true),
  }
}

// ===========================================
// Hook para verificar acceso a rutas
// ===========================================

/**
 * Hook para verificar si el usuario puede acceder a una ruta
 *
 * @example
 * const { puedeAcceder, razonBloqueo } = useAccesoRuta('usuarios')
 */
export function useAccesoRuta(ruta: string) {
  const { puede, rol, isLoading, usuarioActivo } = usePermissions()

  const verificarAcceso = (): { puedeAcceder: boolean; razonBloqueo?: string } => {
    if (isLoading) {
      return { puedeAcceder: false }
    }

    if (!usuarioActivo) {
      return { puedeAcceder: false, razonBloqueo: 'Usuario inactivo' }
    }

    // Mapeo de rutas a permisos requeridos
    const rutasPermisos: Record<string, keyof Permisos> = {
      clientes: 'verClientes',
      movimientos: 'verMovimientos',
      presupuestos: 'verPresupuestos',
      tareas: 'verTareas',
      documentos: 'verDocumentos',
      empleados: 'verEmpleados',
      calendario: 'verCalendario',
      configuracion: 'verConfiguracion',
      usuarios: 'gestionarUsuarios',
      auditoria: 'verAuditoria',
      reportes: 'verReportes',
    }

    const permisoRequerido = rutasPermisos[ruta]

    if (!permisoRequerido) {
      // Ruta no mapeada, permitir por defecto
      return { puedeAcceder: true }
    }

    if (!puede[permisoRequerido]) {
      return {
        puedeAcceder: false,
        razonBloqueo: `No tienes permiso para acceder a esta seccion`,
      }
    }

    return { puedeAcceder: true }
  }

  return {
    ...verificarAcceso(),
    isLoading,
    rol,
  }
}

// ===========================================
// Componente de Contenido Protegido
// ===========================================

interface ProtectedContentProps {
  children: ReactNode
  // Acepta tanto roles legacy como multi-tenant para compatibilidad
  requiredRole?: Rol | Rol[] | RolMultiTenant | RolMultiTenant[]
  requiredPermission?: keyof Permisos
  fallback?: ReactNode
}

/**
 * Componente para proteger contenido basado en rol o permiso
 *
 * @example
 * <ProtectedContent requiredRole="contador_principal">
 *   <AdminPanel />
 * </ProtectedContent>
 *
 * <ProtectedContent requiredPermission="verAuditoria">
 *   <Auditoria />
 * </ProtectedContent>
 */
export function ProtectedContent({
  children,
  requiredRole,
  requiredPermission,
  fallback = null,
}: ProtectedContentProps) {
  const { rol, rolMultiTenant, puede, isLoading, usuarioActivo } = usePermissions()

  if (isLoading) {
    return null // O un spinner
  }

  if (!usuarioActivo) {
    return fallback
  }

  // Verificar rol requerido (soporta legacy y multi-tenant)
  if (requiredRole) {
    const rolesPermitidos = Array.isArray(requiredRole) ? requiredRole : [requiredRole]

    // Verificar si alguno de los roles actuales está en la lista
    const tieneRol =
      (rol && rolesPermitidos.includes(rol as Rol)) ||
      (rolMultiTenant && rolesPermitidos.includes(rolMultiTenant as RolMultiTenant))

    if (!tieneRol) {
      return fallback
    }
  }

  // Verificar permiso requerido
  if (requiredPermission && !puede[requiredPermission]) {
    return fallback
  }

  return <>{children}</>
}
